import { getBaggageDatalayerName, getServiceCategory } from '../utils';
import {
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	OrderAdditionalServiceGdsServiceServiceType,
	CheckoutOrderFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	PassengerSelectedGdsServicesFragment,
	GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment,
	PassengerFragment,
	CheckinPassengerFragment
} from '@websky/graphql';
import { DataLayerService } from '../OrderDataLayer/types';
import WebskyServicesAnalytics from '../WebskyServicesAnalytics';
import { isFareLockService } from '../../ConfirmOption/utils';
import Base from './Base';
import { getHoldDatalayerItem } from './utils';
import { CheckoutCheckinOrder } from '../../DataLayerAnalytics/types';
import { ChangedService, Diff } from './types';

class Gds extends Base {
	protected orderBeforeChange: CheckoutCheckinOrder;
	protected orderAfterChange: CheckoutCheckinOrder;

	constructor(orderBeforeChange: CheckoutCheckinOrder, orderAfterChange: CheckoutCheckinOrder) {
		super(orderBeforeChange, orderAfterChange);
	}

	private readonly convertGdsServicesToDataLayerServices = (
		services: PassengerSelectedGdsServicesFragment[],
		additionalServices: CheckoutOrderFragment['additionalServices'],
		traveller: PassengerFragment | CheckinPassengerFragment,
		travellerIndex: number
	): DataLayerService[] => {
		const converted: DataLayerService[] = [];

		services.forEach(service => {
			const id = service.serviceId;
			const additionalService = additionalServices.gdsServices?.services?.find(s => s.id === id);

			switch (additionalService.type) {
				case OrderAdditionalServiceGdsServiceServiceType.Baggage:
					const baggageAdditionalService = additionalService as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment;
					const baggageService = service as PassengerSelectedGdsServicesFragment;

					converted.push(
						...baggageService.segmentIds?.map(segmentId => ({
							name: getBaggageDatalayerName(baggageAdditionalService),
							price: baggageAdditionalService.price.amount,
							category: getServiceCategory(
								OrderAdditionalServiceGdsServiceServiceType.Baggage,
								baggageAdditionalService.baggageType
							),
							segmentId,
							count: baggageService.count,
							isRecommend: traveller.preselectedServices?.gdsServices?.services?.some(service => {
								return service.serviceId === baggageService.serviceId;
							}),
							travellersTypes: [traveller.type]
						}))
					);
					break;

				case OrderAdditionalServiceGdsServiceServiceType.Meal:
					const mealAdditionalService = additionalService as GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment;
					const mealService = service as PassengerSelectedGdsServicesFragment;

					converted.push(
						...mealService.segmentIds?.map(segmentId => ({
							category: getServiceCategory(OrderAdditionalServiceGdsServiceServiceType.Meal),
							name: mealAdditionalService.name,
							price: mealAdditionalService.price.amount,
							segmentId,
							isRecommend: traveller.preselectedServices?.gdsServices?.services?.some(service => {
								return service.serviceId === mealAdditionalService.id;
							}),
							count: 1,
							travellersTypes: [traveller.type]
						}))
					);
					break;

				case OrderAdditionalServiceGdsServiceServiceType.Vip:
				case OrderAdditionalServiceGdsServiceServiceType.BusinessLounge:
					const vipAdditionalService = additionalService as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;

					if (isFareLockService(vipAdditionalService)) {
						if (travellerIndex === 0) {
							converted.push({
								...getHoldDatalayerItem(vipAdditionalService, [traveller])
							});
						}
					} else {
						service.segmentIds?.forEach(segmentId => {
							converted.push({
								...WebskyServicesAnalytics.selectVip(vipAdditionalService, traveller.id, segmentId),
								travellersTypes: [traveller.type]
							});
						});
					}
					break;
			}
		});

		return converted;
	};

	public diff(): Diff {
		const added: DataLayerService[] = [];
		const removed: DataLayerService[] = [];

		const addedInfo: ChangedService[] = [];
		const removedInfo: ChangedService[] = [];

		this.orderAfterChange?.travellers.forEach((travellerAfterChange, index) => {
			const travellerBeforeChange = this.orderBeforeChange.travellers[index];

			const { added: gdsAdded, removed: gdsRemoved } = this.getTravellersServicesDiff<
				PassengerSelectedGdsServicesFragment
			>(
				travellerBeforeChange.services.gdsServices?.services,
				travellerAfterChange.services.gdsServices?.services,
				service => service.serviceId
			);

			added.push(
				...this.convertGdsServicesToDataLayerServices(
					gdsAdded as PassengerSelectedGdsServicesFragment[],
					this.orderAfterChange.additionalServices,
					travellerAfterChange,
					index
				)
			);
			removed.push(
				...this.convertGdsServicesToDataLayerServices(
					gdsRemoved as PassengerSelectedGdsServicesFragment[],
					this.orderBeforeChange.additionalServices,
					travellerBeforeChange,
					index
				)
			);

			gdsAdded.forEach(service => {
				addedInfo.push({
					serviceId: service.serviceId,
					segmentIds: service.segmentIds,
					passengerIds: [travellerAfterChange.id]
				});
			});
			gdsRemoved.forEach(service => {
				removedInfo.push({
					serviceId: service.serviceId,
					segmentIds: service.segmentIds,
					passengerIds: [travellerBeforeChange.id]
				});
			});
		});

		return { added, removed, addedInfo, removedInfo };
	}
}

export default Gds;
